import { loadLocale } from "@/lib/client/utils/locale";
import { defaultMessages } from "./defaultMessages";
import { availableLocales, isLocaleId } from "./manifest";
import type { LocaleId } from "./manifest";

export type MessagesMap = typeof defaultMessages;
export type MsgId = keyof MessagesMap;

export { availableLocales };
export type { LocaleId };

/**
 * General:
 * - block: Replaces all chars with blocks so you can easily see hard-coded phrases
 * - phraseid: Output phrase ids directly
 * - mark:admin: Replaces all chars with blocks if the ID is an admin prefix (e.g. admin.foo)
 *
 * To test general string length:
 * - 2x: Doubles string length
 * - 3x: Triples string length
 *
 * To test longer words in string (e.g. where wrapping might matter)
 * - wordlen: Increases the length of individual words by 50%
 * - wordlen2x: Increases the length of individual words by 100%
 * - wordlen3x: Increases the length of individual words by 100%
 */
let testQaMode:
  | "block"
  | "phraseid"
  | "2x"
  | "3x"
  | "wordlen"
  | "wordlen2x"
  | "wordlen3x"
  | "mark:admin"
  | undefined = undefined;

export const setTestQaMode = (m: typeof testQaMode): void => {
  switch (m) {
    case "block":
    case "2x":
    case "3x":
    case "wordlen":
    case "wordlen2x":
    case "wordlen3x":
    case "phraseid":
    case "mark:admin":
      testQaMode = m;
      break;
    default:
      testQaMode = undefined;
  }
};

const processMessages = (messages: typeof defaultMessages | MessagesMap): MessagesMap => {
  switch (testQaMode) {
    case "block":
      Object.keys(messages).forEach((k) => {
        const len = messages[k as MsgId].length;
        messages[k as MsgId] = "▚".repeat(len);
      });
      break;

    case "mark:admin":
      Object.keys(messages).forEach((k) => {
        if (k.match(/^admin[_.]{1}/)) {
          const len = messages[k as MsgId].length;
          messages[k as MsgId] = "▚".repeat(len);
        }
      });
      break;

    case "phraseid":
      Object.keys(messages).forEach((k) => {
        messages[k as MsgId] = k;
      });
      break;

    case "2x":
    case "3x": {
      const repeatNum = testQaMode === "3x" ? 2 : 1;

      Object.keys(messages).forEach((k) => {
        const str = messages[k as MsgId] + " ";
        // first the original string
        messages[k as MsgId] = (
          str +
          str
            // reverse it just to appear a bit less like real text
            .split("")
            .reverse()
            .join("")
            .toLocaleLowerCase()
            // and remove special chars that might mess up rendering
            .replace(/[^a-zA-Z0-9 ]/, "x")
            // repeat it depending on 2x or 3x
            .repeat(repeatNum)
        ).trimRight();
      });
      break;
    }
    case "wordlen":
    case "wordlen2x":
    case "wordlen3x": {
      const factor = testQaMode === "wordlen" ? 0.5 : testQaMode === "wordlen3x" ? 3 : 2;

      Object.keys(messages).forEach((k) => {
        messages[k as MsgId] = messages[k as MsgId]
          .split(" ")
          .map((word: string) => {
            if (!word.match(/[a-zA-Z0-9]+/)) {
              return word;
            }

            const wordRepeat = word.toLocaleLowerCase();

            return (
              word + "_" + wordRepeat.repeat(3).substr(0, Math.ceil(wordRepeat.length * factor))
            );
          })
          .join(" ");
      });
      break;
    }
  }

  return messages;
};

export const getDefaultLocaleMessages = (): MessagesMap => {
  return processMessages(defaultMessages);
};

export const getLocaleMessages = async (
  locale: string,
): Promise<{ messages: MessagesMap; dateFnsLocale: Locale }> => {
  if (!isLocaleId(locale)) {
    return { messages: getDefaultLocaleMessages(), dateFnsLocale: await loadLocale(locale) };
  }

  if (locale === "en-US") {
    return { messages: getDefaultLocaleMessages(), dateFnsLocale: await loadLocale(locale) };
  }

  const modules = import.meta.glob("./*.json");
  const messages: Partial<Record<string, unknown>> = (await modules[`./${locale}.json`]()) || {};

  return {
    messages: processMessages({
      ...defaultMessages,
      ...(messages.default || {}),
    }),
    dateFnsLocale: await loadLocale(locale),
  };
};
